'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

import { cn } from '@/lib/utils';

const BARS = [
  8, 10, 6, 12, 14, 14, 12, 6, 12, 18, 6, 8, 14, 18, 16, 22, 10, 12, 14, 12, 10, 16, 22, 16, 14, 28, 18, 14, 14, 14, 8, 26,
  20, 24, 8, 12, 10, 14, 20, 8, 12, 18, 16, 22, 8, 8, 10, 16, 12, 12, 14, 6, 10, 12, 10, 8,
];

const ICONS = {
  play: ['M7 28a1 1 0 0 1-1-1V5a1 1 0 0 1 1.482-.876l20 11a1 1 0 0 1 0 1.752l-20 11A1 1 0 0 1 7 28Z'],
  pause: [
    'M12 6h-2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2Z',
    'M22 6h-2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2Z',
  ],
  mute: [
    'M31 12.41 29.59 11 26 14.59 22.41 11 21 12.41 24.59 16 21 19.59 22.41 21 26 17.41 29.59 21 31 19.59 27.41 16 31 12.41Z',
    'M18 30a1 1 0 0 1-.71-.3L9.67 22H3a1 1 0 0 1-1-1V11a1 1 0 0 1 1-1h6.67l7.62-7.7a1 1 0 0 1 1.42 0A1 1 0 0 1 19 3v26a1 1 0 0 1-1 1Z',
  ],
  sound: [
    'm27.16 8.08-1.53 1.29a10 10 0 0 1-.29 13.23L26.81 24a12 12 0 0 0 .35-15.92Z',
    'M21.58 12a6 6 0 0 1-.18 7.94l1.47 1.36a8 8 0 0 0 .23-10.59Z',
    'M18 30a1 1 0 0 1-.71-.3L9.67 22H3a1 1 0 0 1-1-1V11a1 1 0 0 1 1-1h6.67l7.62-7.7a1 1 0 0 1 1.42 0A1 1 0 0 1 19 3v26a1 1 0 0 1-1 1Z',
  ],
};

function Icon({ name, className }: { name: keyof typeof ICONS; className?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      aria-hidden
      className={cn('shrink-0', className)}>
      {ICONS[name].map((d) => (
        <path
          key={d}
          d={d}
          fill="currentColor"
        />
      ))}
    </svg>
  );
}

const FOCUS = 'outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#9FE0B0]';

type Graph = { ctx: AudioContext; analyser: AnalyserNode; data: Uint8Array };

export default function EdgePlayer({
  src,
  label,
  title,
  className,
}: {
  src: string;
  label?: string;
  title?: string;
  className?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const barsRef = useRef<Array<HTMLSpanElement | null>>([]);
  const graphRef = useRef<Graph | null>(null);
  const manualPause = useRef(false);
  const reduce = useReducedMotion();
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const play = useCallback(() => {
    audioRef.current?.play().catch(() => setPlaying(false));
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = true;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.pause();
      graphRef.current?.ctx.close().catch(() => {});
      graphRef.current = null;
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    const audio = audioRef.current;
    if (!root || !audio) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          audio.pause();
          return;
        }
        if (entry.intersectionRatio >= 0.4 && !manualPause.current && !reduce && audio.paused) play();
      },
      { threshold: [0, 0.4] },
    );
    observer.observe(root);
    return () => observer.disconnect();
  }, [play, reduce]);

  useEffect(() => {
    const bars = barsRef.current;
    const rest = () => bars.forEach((bar, i) => bar && (bar.style.height = `${BARS[i] / 8}rem`));
    if (!playing || reduce) {
      rest();
      return;
    }
    let frame = 0;
    const tick = (now: number) => {
      const graph = graphRef.current;
      const live = graph && !muted;
      if (live) graph.analyser.getByteFrequencyData(graph.data);
      const t = now / 1000;
      bars.forEach((bar, i) => {
        if (!bar) return;
        const px = live
          ? 4 + Math.pow(graph.data[1 + Math.floor((i * 72) / BARS.length)] / 255, 1.2) * 24
          : BARS[i] * (0.4 + 0.6 * (0.5 + 0.5 * Math.sin(t * (2.4 + ((i * 7) % 11) / 4) + i * 1.7)));
        bar.style.height = `${Math.max(3, px) / 8}rem`;
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, muted, reduce]);

  const startWithSound = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!graphRef.current && typeof window.AudioContext === 'function') {
      try {
        const ctx = new AudioContext();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        ctx.createMediaElementSource(audio).connect(analyser);
        analyser.connect(ctx.destination);
        graphRef.current = { ctx, analyser, data: new Uint8Array(analyser.frequencyBinCount) };
      } catch {
        graphRef.current = null;
      }
    }
    graphRef.current?.ctx.resume().catch(() => {});
    audio.muted = false;
    audio.currentTime = 0;
    manualPause.current = false;
    setMuted(false);
    play();
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.muted) {
      startWithSound();
    } else if (audio.paused) {
      manualPause.current = false;
      play();
    } else {
      manualPause.current = true;
      audio.pause();
    }
  };

  const toggleSound = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.muted) {
      startWithSound();
      return;
    }
    audio.muted = true;
    setMuted(true);
  };

  const soundLabel = muted ? 'Unmute' : 'Mute';

  const soundButton = (variant: 'inline' | 'tile') => (
    <button
      type="button"
      onClick={toggleSound}
      aria-label={soundLabel}
      aria-pressed={!muted}
      className={cn(
        'relative flex shrink-0 items-center justify-center text-white',
        FOCUS,
        variant === 'inline'
          ? 'h-[4.5rem] w-[4.5rem] rounded-full border border-white/[0.12] lg:hidden'
          : 'hidden h-[7.5rem] w-[7.5rem] rounded-[1.5rem] border border-white/[0.12] bg-[#0A0E0C]/[0.84] backdrop-blur-[3rem] lg:flex',
      )}>
      {muted && !reduce && (
        <span
          aria-hidden
          className="rze-pulse pointer-events-none absolute inset-[-1px] rounded-[inherit] border border-[rgba(159,224,176,0.7)]"
        />
      )}
      <Icon
        name={muted ? 'mute' : 'sound'}
        className={variant === 'inline' ? 'h-[2.5rem] w-[2.5rem]' : 'h-[3.5rem] w-[3.5rem]'}
      />
    </button>
  );

  return (
    <div
      ref={rootRef}
      className={cn('flex items-center gap-[2rem]', className)}>
      <audio
        ref={audioRef}
        src={src}
        loop
        muted
        playsInline
        preload="none"
        crossOrigin="anonymous"
      />
      <div className="flex w-full min-w-0 items-center gap-[1.5rem] rounded-[0.75rem] border border-white/[0.12] bg-[#0A0E0C]/[0.56] py-[1rem] pl-[1rem] pr-[2rem] backdrop-blur-[1.5rem] lg:h-[7.5rem] lg:w-auto lg:gap-[2rem] lg:rounded-[1.5rem] lg:bg-[#0A0E0C]/[0.84] lg:py-0 lg:pl-[1.5rem] lg:pr-[3rem] lg:backdrop-blur-[3rem]">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing && !muted ? 'Pause' : 'Play'}
          className={cn(
            'flex h-[4.5rem] w-[4.5rem] shrink-0 items-center justify-center rounded-full bg-white text-black lg:h-[5.5rem] lg:w-[5.5rem]',
            FOCUS,
          )}>
          <Icon
            name={playing && !muted ? 'pause' : 'play'}
            className="h-[2rem] w-[2rem] lg:h-[2.5rem] lg:w-[2.5rem]"
          />
        </button>
        <div className="flex min-w-0 flex-1 flex-col gap-[0.5rem] overflow-hidden font-geist lg:flex-none lg:flex-row lg:items-center lg:gap-[2rem] lg:overflow-visible">
          {(label || title) && (
            <div className="flex flex-col whitespace-nowrap lg:w-[31.75rem]">
              {label && <p className="m-0 text-[1.75rem] font-[500] leading-[2.5rem] text-white/[0.72]">{label}</p>}
              {title && (
                <p className="m-0 hidden overflow-hidden text-ellipsis text-[2rem] font-[600] leading-[3rem] text-white lg:block">
                  {title}
                </p>
              )}
            </div>
          )}
          <div
            aria-hidden
            className="flex h-[2.4rem] items-center gap-[0.375rem] lg:h-[4rem]">
            {BARS.map((h, i) => (
              <span
                key={i}
                ref={(el) => {
                  barsRef.current[i] = el;
                }}
                className="w-[0.375rem] shrink-0 rounded-[0.25rem] bg-white"
                style={{ height: `${h / 8}rem` }}
              />
            ))}
          </div>
        </div>
        {soundButton('inline')}
      </div>
      {soundButton('tile')}
      <style
        jsx
        global>{`
        .rze-pulse {
          animation: rze-pulse 2.4s cubic-bezier(0.16, 1, 0.3, 1) infinite;
        }
        @keyframes rze-pulse {
          from {
            transform: scale(1);
            opacity: 1;
          }
          to {
            transform: scale(1.16);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
